<template>
  <form class="max-w-5xl" @submit.prevent="emit('save')">
    <div class="mt-10 mb-40 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6" :class="{'opacity-30 disabled': banner.deletedAt || banner.isEnabled === false}">
      <form-input
        v-model.trim="banner.title"
        label="Name"
        class="md:col-span-4"
        :error-msg="[
          ...result.title.$messages,
          ...result.titleLength.$messages
        ].join(' | ')"
      />

      <!-- Character counter helper text -->
      <div v-if="banner.title" class="md:col-span-4 -mt-4 mb-2 text-sm">
        <span
          :class="{
            'text-green-600': banner.title.length <= 40,
            'text-yellow-600': banner.title.length > 40 && banner.title.length <= 60,
            'text-red-600': banner.title.length > 60 && banner.title.length <= 85,
            'text-red-800 font-bold': banner.title.length > 85
          }"
        >
          {{ banner.title.length }}/85 characters
          {{ 
            banner.title.length <= 40 ? '✅ Optimal' :
            banner.title.length <= 60 ? '⚠️ Long' :
            banner.title.length <= 85 ? '❌ Too long' : '🚫 Exceeds limit'
          }}
        </span>
      </div>

      <form-textarea v-model="banner.content" label="Content" class="col-span-full" :error-msg="result.content.$messages.join(' | ')" />

      <form-image-upload v-model="banner.rightBlockImage" label="Right block image" class="col-span-2" />

      <form-input v-model.number="banner.order" label="Order" type="number" class="md:col-span-2" :error-msg="result.order.$messages.join(' | ')" />

      <form-color-picker v-model="banner.textColor" label="Text color" class="col-span-full" disable-gradient :error-msg="result.textColor.$messages.join(' | ')" />

      <form-input v-model.trim="banner.actionButtonName" label="Action name" class="md:col-span-4" :error-msg="result.actionButtonName.$messages.join(' | ')" />

      <form-radio-group
        v-model="banner.actionType"
        label="Action type"
        :items="[
          { name: 'Open URL', value: 'url' },
          { name: 'Open Paywall', value: 'paywall' }
        ]"
        class="col-span-full"
      />

      <form-input v-if="banner.actionType === 'url'" v-model.trim="banner.link" label="Link" class="md:col-span-4" :error-msg="result.link.$messages.join(' | ')" />

      <form-select
        v-model="banner.conditions"
        label="Condition"
        placeholder="Select a condition"
        :items="conditions"
        :error-msg="result.conditions.$messages.join(' | ')"
        class="col-span-4"
      />

      <form-switch
        v-model="banner.isCategoryBanner"
        label="Is category banner"
        class="col-span-4"
      />

      <form-select
        v-if="banner.isCategoryBanner"
        v-model="banner.categoryId"
        label="Category"
        placeholder="Select a category"
        :items="stats?.categories"
        :error-msg="result.categoryId.$messages.join(' | ')"
        class="col-span-4"
      />

      <form-radio-group
        v-model="backgroundType"
        label="Background type"
        :items="[
          { name: 'Color', value: 'color' },
          { name: 'Image', value: 'image' }
        ]"
        class="col-span-full"
      />

      <form-color-picker v-if="backgroundType === 'color'" v-model="banner.backgroundColor" label="Background color" class="col-span-full" :error-msg="result.backgroundColor.$messages.join(' | ')" />
      <form-image-upload v-else v-model="banner.backgroundImage" label="Background image" class="col-span-2" :error-msg="result.backgroundImage.$messages.join(' | ')" />

      <!-- Translations Section -->
      <div class="col-span-full mt-8 border-t pt-6">
        <h3 class="text-lg font-medium text-gray-900 mb-4">
          Translations
        </h3>
        <p class="text-sm text-gray-500 mb-4">
          Add translations for different languages. English (en) uses the default fields above.
        </p>

        <!-- Language Tabs -->
        <div class="border-b border-gray-200 mb-4">
          <nav class="-mb-px flex space-x-4">
            <button
              v-for="lang in availableLanguages"
              :key="lang.code"
              type="button"
              :class="[
                selectedLanguage === lang.code
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                'whitespace-nowrap border-b-2 py-2 px-4 text-sm font-medium'
              ]"
              @click="selectedLanguage = lang.code"
            >
              {{ lang.name }}
            </button>
          </nav>
        </div>

        <!-- Translation Fields -->
        <div v-if="selectedLanguage" class="space-y-4">
          <form-input
            v-model.trim="translationTitle"
            :label="`Title (${selectedLanguage})`"
            class="md:col-span-4"
            placeholder="Leave empty to use default English text"
          />

          <form-textarea
            v-model="translationContent"
            :label="`Content (${selectedLanguage})`"
            class="col-span-full"
            placeholder="Leave empty to use default English text"
          />

          <form-input
            v-model.trim="translationActionButtonName"
            :label="`Action Button Name (${selectedLanguage})`"
            class="md:col-span-4"
            placeholder="Leave empty to use default English text"
          />
        </div>
      </div>
    </div>
  </form>
</template>

<script setup>
import { reactive, ref, watch, computed } from 'vue';
import { getStats } from '../composables/useProgramStats';
import FormInput from './FormInput.vue';
import FormRadioGroup from './FormRadioGroup.vue';
import FormTextarea from './FormTextarea.vue';
import FormSelect from './FormSelect.vue';
import FormImageUpload from './FormImageUpload.vue';
import FormSwitch from './FormSwitch.vue';
import FormColorPicker from './FormColorPicker.vue';
import useValidate from 'vue-tiny-validate';

const banner = defineModel('banner', {
  type: Object,
  default: () => ({
    title: null,
    content: null,
    actionButtonName: null,
    actionType: 'url',
    link: null,
    backgroundColor: '#ffffff',
    backgroundImage: '',
    textColor: '#000000',
    rightBlockImage: '',
    order: 0,
    conditions: null,
    isCategoryBanner: false,
    categoryId: null,
    translations: {}
  })
});

// Initialize translations if not exists
if (!banner.value.translations) {
  banner.value.translations = {};
}

// Available languages for translation
const availableLanguages = [
  { code: 'es', name: 'Spanish' },
  { code: 'sk', name: 'Slovak' },
  { code: 'ro', name: 'Romanian' },
  { code: 'pl', name: 'Polish' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'de', name: 'German' },
  { code: 'cz', name: 'Czech' }
];

const selectedLanguage = ref('es'); // Default to Spanish

// Computed properties for current language translation
const translationTitle = computed({
  get: () => banner.value.translations?.[selectedLanguage.value]?.title || '',
  set: value => {
    if (!banner.value.translations) {
      banner.value.translations = {};
    }
    if (!banner.value.translations[selectedLanguage.value]) {
      banner.value.translations[selectedLanguage.value] = {};
    }
    banner.value.translations[selectedLanguage.value].title = value;
    // Clean up empty translations
    if (!value && !banner.value.translations[selectedLanguage.value].content && 
        !banner.value.translations[selectedLanguage.value].actionButtonName) {
      delete banner.value.translations[selectedLanguage.value];
    }
  }
});

const translationContent = computed({
  get: () => banner.value.translations?.[selectedLanguage.value]?.content || '',
  set: value => {
    if (!banner.value.translations) {
      banner.value.translations = {};
    }
    if (!banner.value.translations[selectedLanguage.value]) {
      banner.value.translations[selectedLanguage.value] = {};
    }
    banner.value.translations[selectedLanguage.value].content = value;
    // Clean up empty translations
    if (!value && !banner.value.translations[selectedLanguage.value].title && 
        !banner.value.translations[selectedLanguage.value].actionButtonName) {
      delete banner.value.translations[selectedLanguage.value];
    }
  }
});

const translationActionButtonName = computed({
  get: () => banner.value.translations?.[selectedLanguage.value]?.actionButtonName || '',
  set: value => {
    if (!banner.value.translations) {
      banner.value.translations = {};
    }
    if (!banner.value.translations[selectedLanguage.value]) {
      banner.value.translations[selectedLanguage.value] = {};
    }
    banner.value.translations[selectedLanguage.value].actionButtonName = value;
    // Clean up empty translations
    if (!value && !banner.value.translations[selectedLanguage.value].title && 
        !banner.value.translations[selectedLanguage.value].content) {
      delete banner.value.translations[selectedLanguage.value];
    }
  }
});

// Determine initial background type based on existing data
const getInitialBackgroundType = () => {
  if (banner.value.backgroundColor) return 'color';
  if (banner.value.backgroundImage) return 'image';
  return 'color'; // default to color if both are empty
};


const backgroundType = ref(getInitialBackgroundType());
// Temporary storage for switching background type
let savedBackgroundColor = banner.value.backgroundColor;
let savedBackgroundImage = banner.value.backgroundImage;

// Initialize actionType if not set
if (!banner.value.actionType) {
  banner.value.actionType = 'url';
}

// Update backgroundType when banner changes (e.g., when editing existing banner)
watch(() => [ banner.value.backgroundColor, banner.value.backgroundImage ], () => {
  if (banner.value.backgroundColor && !backgroundType.value) {
    backgroundType.value = 'color';
  } else if (banner.value.backgroundImage && !backgroundType.value) {
    backgroundType.value = 'image';
  }
}, { immediate: false });

const emit = defineEmits([ 'save', 'update:backgroundType' ]);

defineExpose({
  onValidate,
  getResult
});

const rules = reactive({
  title: {
    name: 'required',
    test: value => Boolean(value),
    message: 'Title must not be empty.'
  },
  titleLength: {
    name: 'maxLength',
    test: () => {
      const title = banner.value.title || '';
      return title.length <= 85;
    },
    message: 'Title must not exceed 85 characters for optimal display.'
  },
  content: {
    name: 'required',
    test: value => Boolean(value),
    message: 'Content must not be empty.'
  },
  actionButtonName: {
    name: 'required',
    test: value => Boolean(value),
    message: 'Action name must not be empty.'
  },
  link: {
    name: 'required',
    test: value => {
      if (banner.value.actionType !== 'url') {
        return true;
      }
      return Boolean(value);
    },
    message: 'Link name must not be empty.'
  },
  backgroundColor: {
    name: 'validColor',
    test: value => {
      if (backgroundType.value !== 'color') {
        return true;
      }
      if (!value) return false;
      // Check for hex color
      if (/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value)) {
        return true;
      }
      // Check for rgba/rgb
      if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(?:,\s*[\d.]+\s*)?\)$/.test(value)) {
        return true;
      }
      // Check for linear-gradient
      if (value.startsWith('linear-gradient(')) {
        return true;
      }
      return false;
    },
    message: 'Background color must be a valid HEX, RGB, RGBA or gradient.'
  },
  backgroundImage: {
    name: 'required',
    test: value => {
      if (backgroundType.value !== 'image') {
        return true;
      }
      return Boolean(value);
    },
    message: 'Image icon must not be empty.'
  },
  textColor: {
    name: 'validColor',
    test: value => {
      if (!value) return true;
      // Check for hex color
      if (/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value)) {
        return true;
      }
      // Check for rgba/rgb
      if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(?:,\s*[\d.]+\s*)?\)$/.test(value)) {
        return true;
      }
      return false;
    },
    message: 'Color must be a valid HEX, RGB or RGBA.'
  },
  conditions: {
    name: 'required',
    test: value => Boolean(value),
    message: 'Conditions must not be empty.'
  },
  categoryId: {
    name: 'required',
    test: value => {
      if (!banner.value.isCategoryBanner) {
        return true;
      }
      return Boolean(value);
    },
    message: 'Category must be selected.'
  },
  order: {
    name: 'required',
    test: value => typeof value === 'number' && value >= 0,
    message: 'Order must be a number greater than or equal to 0.'
  }
});
const options = reactive({
  autoTest: true
});
const { result } = useValidate(banner, rules, options);

async function onValidate() {
  // Clean up unused background field before validation
  if (backgroundType.value === 'color') {
    banner.value.backgroundImage = null;
  } else {
    banner.value.backgroundColor = null;
  }

  const isValid = await result.value.$test();
  return isValid;
}

function getResult() {
  return result.value;
}

const conditions = [
  {
    id: 'noSubscription',
    name: 'No subscription'
  },
  {
    id: 'hourSubscription',
    name: '1 hour subscription'
  },
  {
    id: 'monthlySubscription',
    name: 'Monthly subscription'
  },
  {
    id: 'newProgramInCategory',
    name: 'New program in category'
  },
  {
    id: 'newFeature',
    name: 'New featute'
  },
  {
    id: 'personalizedBannerForTheCategory',
    name: 'Personalized Banner for the Category'
  }
];

const stats = getStats();

watch(backgroundType, newValue => {
  if (newValue === 'color') {
    // Save image before clearing
    savedBackgroundImage = banner.value.backgroundImage;
    result.value.backgroundImage.$reset();
    banner.value.backgroundImage = '';
    // Restore color if switching back
    if (!banner.value.backgroundColor && savedBackgroundColor) {
      banner.value.backgroundColor = savedBackgroundColor;
    }
  } else {
    // Save color before clearing
    savedBackgroundColor = banner.value.backgroundColor;
    result.value.backgroundColor.$reset();
    banner.value.backgroundColor = '';
    // Restore image if switching back
    if (!banner.value.backgroundImage && savedBackgroundImage) {
      banner.value.backgroundImage = savedBackgroundImage;
    }
  }
  emit('update:backgroundType', newValue);
});

watch(() => banner.value.isCategoryBanner, newValue => {
  if (!newValue) {
    result.value.categoryId.$reset();
    banner.value.categoryId = null;
  }
});

watch(() => banner.value.actionType, newValue => {
  if (newValue === 'paywall') {
    result.value.link.$reset();
    banner.value.link = null;
  }
});
</script>
